import { useState } from 'react';
import { Shield, ShieldCheck, Loader2, Copy, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { PlatformLogo } from '@/components/PlatformLogo';
import { enrollTotpFactor, verifyTotpEnrollment } from '@/lib/mfaEnrollment';
import { emitMfaLifecycleEvent } from '@/lib/auditMfaEvent';

interface VendorMFAEnforcementProps {
  vendorName: string;
  organizationName: string;
  onMFAEnabled: () => void;
}

export function VendorMFAEnforcement({ vendorName, organizationName, onMFAEnabled }: VendorMFAEnforcementProps) {
  const { toast } = useToast();
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollmentData, setEnrollmentData] = useState<{
    id: string;
    qr: string;
    secret: string;
  } | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleEnrollMFA = async () => {
    setIsEnrolling(true);
    try {
      const result = await enrollTotpFactor();

      if (result.status === 'already-enabled') {
        toast({
          title: 'MFA already enabled',
          description: 'Two-factor authentication is already set up for your account.',
        });
        onMFAEnabled();
        return;
      }

      setEnrollmentData(result.enrollment);
      setIsDialogOpen(true);
    } catch (error: any) {
      toast({
        title: 'Error setting up MFA',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleVerifyEnrollment = async () => {
    if (!enrollmentData || verifyCode.length !== 6) return;

    setIsVerifying(true);
    try {
      await verifyTotpEnrollment(enrollmentData.id, verifyCode);

      toast({
        title: 'MFA enabled',
        description: 'Two-factor authentication has been successfully enabled.',
      });

      setIsDialogOpen(false);
      setEnrollmentData(null);
      setVerifyCode('');
      onMFAEnabled();
    } catch (error: any) {
      toast({
        title: 'Verification failed',
        description: error.message || 'Invalid verification code. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const copySecret = async () => {
    if (!enrollmentData?.secret) return;
    await navigator.clipboard.writeText(enrollmentData.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDialogClose = async (open: boolean) => {
    if (!open && enrollmentData) {
      // Cancel enrollment if dialog is closed without verifying
      try {
        await supabase.auth.mfa.unenroll({ factorId: enrollmentData.id });
        await emitMfaLifecycleEvent({
          eventType: 'mfa.factor_enroll_cancelled',
          factorId: enrollmentData.id,
        });
      } catch (error) {
        // Ignore errors on cleanup
      }
      setEnrollmentData(null);
      setVerifyCode('');
    }
    setIsDialogOpen(open);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-950 via-emerald-900 to-teal-950 p-4">
      <Card className="w-full max-w-lg border-emerald-700/30 bg-background/95 backdrop-blur">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
              <PlatformLogo variant="green" className="h-10 w-10" />
            </div>
          </div>
          <CardTitle className="text-2xl">Two-Factor Authentication Required</CardTitle>
          <CardDescription className="text-base mt-2">
            To access <span className="font-semibold text-emerald-600">{vendorName}</span>'s supplier portal, 
            you need to enable two-factor authentication.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert className="border-amber-500/50 bg-amber-500/10">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-700 dark:text-amber-400">Security Requirement</AlertTitle>
            <AlertDescription className="text-amber-600/90 dark:text-amber-300/90">
              <span className="font-medium">{organizationName}</span> requires all team members to have 
              two-factor authentication enabled for enhanced security.
            </AlertDescription>
          </Alert>

          <div className="p-4 rounded-lg bg-muted/50 space-y-3">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                <span className="text-sm font-semibold text-emerald-600">1</span>
              </div>
              <div>
                <p className="font-medium">Download an authenticator app</p>
                <p className="text-sm text-muted-foreground">
                  Google Authenticator, Authy, or 1Password recommended
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                <span className="text-sm font-semibold text-emerald-600">2</span>
              </div>
              <div>
                <p className="font-medium">Scan the QR code</p>
                <p className="text-sm text-muted-foreground">
                  Use your authenticator app to scan the code we'll show you
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                <span className="text-sm font-semibold text-emerald-600">3</span>
              </div>
              <div>
                <p className="font-medium">Enter the verification code</p>
                <p className="text-sm text-muted-foreground">
                  Confirm setup by entering the 6-digit code from your app
                </p>
              </div>
            </div>
          </div>

          <Button 
            onClick={handleEnrollMFA} 
            disabled={isEnrolling}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            size="lg"
          >
            {isEnrolling ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Setting up...
              </>
            ) : (
              <>
                <Shield className="h-4 w-4 mr-2" />
                Set Up Two-Factor Authentication
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Enrollment Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app (like Google Authenticator, Authy, or 1Password)
            </DialogDescription>
          </DialogHeader>

          {enrollmentData && (
            <div className="space-y-6 py-4">
              {/* QR Code */}
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-lg">
                  <img
                    src={enrollmentData.qr}
                    alt="QR Code for MFA setup"
                    className="w-48 h-48"
                  />
                </div>
              </div>

              {/* Manual Entry */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">
                  Or enter this code manually:
                </Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-muted rounded text-sm font-mono break-all">
                    {enrollmentData.secret}
                  </code>
                  <Button variant="outline" size="icon" onClick={copySecret}>
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Verification */}
              <div className="space-y-3">
                <Label htmlFor="verify-code">Enter the 6-digit code from your app</Label>
                <Input
                  id="verify-code"
                  placeholder="000000"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="text-center text-2xl tracking-widest font-mono"
                />
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleVerifyEnrollment}
                  disabled={verifyCode.length !== 6 || isVerifying}
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="h-4 w-4 mr-2" />
                      Verify and Enable
                    </>
                  )}
                </Button>
              </div>

              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Important</AlertTitle>
                <AlertDescription>
                  Save your recovery codes in a safe place. You'll need them if you lose access to your authenticator app.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
